import { avisEncoreCochable, echeanceAvis } from './avis.js';
import { ajouterJours, estApres, formatCourt, joursEntre } from './dates.js';
import { attenteEnCours, aupres, detailEtape, etapeDe, libelleEtape, livraisonEstimee, nomFournisseurs, segmentDe } from './etapes.js';
import { appliquerEtapesCochees } from './etapes-cochees.js';
import { chezHiolle } from './enlevement.js';
import { estCommandeFortemix } from './fortemix.js';
import { ajouterJoursOuvres } from './jours-ouvres.js';
// `relances.js` ne dépend pas d'`actions.js` : l'import ne referme aucun cycle.
import { expedieLuiMeme } from './relances.js';
import { CANAUX_CLIENT, CANAUX_ENVOI, LIBELLE_ATTENTE_CANAL, LIBELLE_ECHEC_CANAL, RESOLUTION_LITIGE_PAR_DEFAUT, LIBELLE_MOTIF_RELANCE, } from './types.js';
/** Ce que l'échec de chaque canal signifie concrètement, pour la fiche. */
const DETAIL_ECHEC_CANAL = {
    fournisseur: "L'envoi de la commande a échoué : aucun mail n'est parti de contact@, et le " +
        "fournisseur n'a donc rien reçu. La commande reste à transmettre, et l'opération est " +
        'rejouable depuis la fiche.',
    expedition: "L'envoi automatique du mail d'expédition a échoué : le client n'a pas été prévenu que sa " +
        "commande partait. L'envoi est rejouable depuis la fiche.",
    tracking: "L'envoi automatique du mail de tracking a échoué : le client n'a pas reçu son numéro de " +
        'suivi. L\'envoi est rejouable depuis la fiche.',
};
/** Ce qu'un mail client non préparé veut dire, pour la fiche. */
const DETAIL_ATTENTE_CANAL = {
    fournisseur: "La commande n'a pas encore été transmise au fournisseur.",
    expedition: "Une date d'expédition est connue, et le client ne l'a pas. Le mail s'ouvre depuis la "
        + 'fiche, sous « Prévenir le client » : il est composé pour vous, vous le relisez, et il '
        + 'part depuis contact@ quand vous cliquez sur « Envoyer ».',
    tracking: "Un numéro de suivi est connu, et le client ne l'a pas. Le mail s'ouvre depuis la fiche, "
        + 'sous « Prévenir le client » : il est composé pour vous, vous le relisez, et il part '
        + 'depuis contact@ quand vous cliquez sur « Envoyer ».',
};
/**
 * Libellés transporteur qui signalent un ENNUI, plutôt qu'un point de suivi.
 *
 * Ce sont les seuls statuts qui alertent **le jour même**, sans attendre le
 * seuil de silence : un transporteur qui annonce un problème n'a pas besoin de
 * se taire trois jours pour qu'on le croie. Le reste du transit se juge sur le
 * silence (cf. `actionsDe`, étape `transit`).
 *
 * Deux familles :
 *
 * - **la livraison attend quelque chose** — « Différé », « Att rglt » (attente
 *   de règlement). Relevés sur les données réelles de Mosaik ;
 * - **la livraison a échoué** — « Not Delivered » chez DSV, et ses équivalents
 *   dans les langues que les transporteurs servent. Constaté le 18/09/2026 sur
 *   FAC-20260810-39421 : DSV rendait « Not Delivered » à Cerfontaine depuis
 *   deux jours, et la commande passait pour un transit ordinaire.
 *
 * **Comparaison sur le libellé APLATI** (sans accents ni casse) : les
 * transporteurs écrivent « Différé », « DIFFERE » et « differé » pour la même
 * chose, et leurs libellés anglais arrivent en capitales une fois sur deux. Les
 * motifs sont donc écrits sans accent et en minuscules.
 *
 * La détection par motif est fragile par nature : cette liste est faite pour
 * s'allonger, et c'est le seul endroit à modifier.
 */
export const MOTIFS_LIVRAISON_BLOQUEE = [
    // La livraison attend — français, relevé sur les données Mosaik.
    /\bdiffere/,
    /\batt rglt\b/,
    // La livraison a échoué — français. « pas livré » vit ici avec « non livré » :
    // c'est la même chose, et les séparer donnait deux traitements à un seul fait
    // (« non livré » alertait, « pas livré » passait pour un transit ordinaire).
    /\b(non|pas)\s+livr/,
    /echec\s+(de\s+)?livraison/,
    /livraison\s+(impossible|refusee|annulee|echouee)/,
    /\brefus(e|ee|ed)?\s+par\s+le\s+(client|destinataire)/,
    // Anglais : DSV et DHL rendent leurs libellés en anglais quelle que soit la
    // langue demandée. « not delivered » est le statut d'un passage manqué.
    /\bnot\s+delivered\b/,
    /\bundelivered\b/,
    /\bdelivery\s+(failed|refused|exception|attempt\s+failed)\b/,
    // Allemand, italien, espagnol — mêmes pays que ceux où Mosaik expédie.
    /\bnicht\s+zugestellt\b/,
    /\bzustellung\s+fehlgeschlagen\b/,
    /\bnon\s+consegnat/,
    /\bno\s+entregad/,
];
/**
 * Forme comparable d'un libellé : sans accents, sans casse, espaces réduits.
 *
 * Recopiée ici plutôt qu'importée de `livraison.ts` : c'est ce module-là qui
 * dépend de celui-ci, et l'inverse fermerait le cycle.
 */
function aplatirStatut(texte) {
    return texte
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Libellés qui portent un motif de blocage mais annoncent une livraison CALÉE.
 *
 * DHL Freight range sous « Différé » tout ce qui n'est pas une livraison
 * immédiate, y compris la bonne nouvelle : « Différé - Rendez-vous de livraison
 * pris ». Constaté le 01/10/2026 sur #MF3400 — rendez-vous fixé au lendemain,
 * et la commande remontait en rouge sous « Livraison bloquée ». Le rendez-vous
 * pris lève l'attente que signalait « Différé - En attente de rendez-vous ».
 *
 * Testés AVANT `MOTIFS_LIVRAISON_BLOQUEE`, sur le même libellé aplati.
 */
const MOTIFS_LIVRAISON_PROGRAMMEE = [
    /\brendez-?\s?vous\s+(de\s+livraison\s+)?(pris|fixe|confirme)/,
    /\brdv\s+(pris|fixe|confirme)/,
];
export function estLivraisonBloquee(statut) {
    if (!statut)
        return false;
    const texte = aplatirStatut(statut);
    if (MOTIFS_LIVRAISON_PROGRAMMEE.some((m) => m.test(texte)))
        return false;
    return MOTIFS_LIVRAISON_BLOQUEE.some((m) => m.test(texte));
}
/**
 * Actions dues sur une commande, de la plus grave à la moins grave.
 *
 * Fonction pure : tout vient des paramètres, rien n'est lu ailleurs. Les seuils
 * arrivent par `reglages`, la date du jour par `aujourdhui` — c'est ce qui rend
 * les règles testables et reproductibles.
 */
export function actionsDe(c, reglages, aujourdhui) {
    const s = reglages.seuils;
    const etape = etapeDe(c, aujourdhui);
    const out = [];
    const noms = nomFournisseurs(c.fournisseurs);
    // Un envoi qui a échoué veut dire que le fournisseur ou le client n'a jamais
    // été prévenu. Placé avant la mise en attente ci-dessous : c'est un défaut du
    // système lui-même, il ne doit jamais être masqué.
    for (const canal of CANAUX_ENVOI) {
        if (c.envois[canal] !== 'echec')
            continue;
        out.push({
            cle: 'mail',
            canal,
            titre: LIBELLE_ECHEC_CANAL[canal],
            gravite: 3,
            badge: 'à rejouer',
            detail: DETAIL_ECHEC_CANAL[canal],
        });
    }
    // Même défaut, sur le mail d'un colis supplémentaire : son client n'a pas
    // son numéro.
    for (const k of c.colisAPrevenir ?? []) {
        if (!k.echec)
            continue;
        out.push({
            cle: 'mail',
            canal: 'tracking',
            colis: k.tracking,
            titre: `Mail du colis ${k.tracking} non parti`,
            gravite: 3,
            badge: 'à rejouer',
            detail: `L'envoi du numéro de suivi ${k.tracking} a échoué : le client ne l'a pas reçu.`,
        });
    }
    /*
     * **Shopify a changé la commande après sa transmission.** Un remboursement
     * ou une annulation arrivé une fois la commande partie chez le fournisseur
     * ne change rien chez lui tant qu'on ne le prévient pas : il fabriquera et
     * livrera des articles que le client ne paiera pas.
     *
     * Placé avec les échecs d'envoi, au-dessus de toute mise en attente : c'est
     * de l'argent qui part, et rien ne doit le masquer. L'action reste due
     * jusqu'à ce qu'on indique, depuis la fiche, que le fournisseur est prévenu.
     */
    // Jamais sur une commande livrée : un remboursement après livraison est un
    // retour, et il n'y a plus rien à arrêter chez le fournisseur.
    if (c.dateEnvoiFournisseur && c.ajustementsASignaler && !c.livre) {
        out.push({
            cle: 'shopify',
            titre: `Prévenir ${aupres(c)} : ${c.ajustementsASignaler}`,
            gravite: 3,
            badge: c.annulee ? 'annulée' : 'remboursement',
            detail: `La commande a été transmise${noms ? ` à ${noms}` : ''}, puis modifiée dans Shopify `
                + `(${c.ajustementsASignaler}). Le fournisseur n'en sait rien : prévenez-le, puis indiquez-le `
                + 'depuis la fiche.',
        });
    }
    /*
     * **Une commande annulée ne réclame plus rien** — ni transmission, ni
     * relance, ni suivi. Seuls restent les signaux ci-dessus.
     */
    if (c.annulee)
        return trierParGravite(out);
    /**
     * Commande PLANIFIÉE : le départ est annoncé pour un jour à venir.
     *
     * Elle ne réclame rien tant que ce jour n'est pas arrivé. Prévenir le client
     * d'un départ qui n'a pas eu lieu, ou réclamer un numéro de suivi d'un colis
     * qui n'est pas parti, c'est du travail qu'on ne peut pas faire — et qui
     * revient chaque matin dans « À faire » jusqu'à la date.
     *
     * Elle passe donc dans « En cours », et revient d'elle-même le jour dit :
     * `etapeDe` la fait alors basculer de `planifiee` à `expediee`, et tout ce
     * qui suit se réveille sans qu'on ait rien à faire.
     *
     * **Même place que la mise en attente ci-dessous, et pour la même raison.**
     * Les échecs d'envoi restent AU-DESSUS : ils disent que le système lui-même
     * a manqué quelque chose, et rien ne doit les masquer — pas même une date
     * future. C'est l'invariant que ce fichier tient depuis l'origine.
     */
    if (etape === 'planifiee')
        return out;
    /**
     * Mise en attente datée, posée à la main.
     *
     * Deux états, et un seul jour les sépare :
     *
     * - **avant l'échéance** — la commande ne réclame rien. Elle passe donc dans
     *   « En cours », avec sa raison d'attendre affichée à la place ;
     * - **le jour venu, et après** — elle lève une action, et revient donc dans
     *   « À faire ». C'est ce qui distingue une attente d'un oubli : sans ce
     *   retour, mettre en attente serait un moyen de perdre une commande.
     *
     * Placée avant tout le reste, sauf les échecs d'envoi ci-dessus : ceux-là
     * disent que le système lui-même a manqué quelque chose, et rien ne doit les
     * masquer — pas même une attente délibérée.
     */
    if (c.attenteJusquAu) {
        if (estApres(c.attenteJusquAu, aujourdhui))
            return out;
        const depuis = joursEntre(c.attenteJusquAu, aujourdhui);
        out.push({
            cle: 'attente',
            titre: c.attenteMotif ? `Attente échue — ${c.attenteMotif}` : 'Attente échue',
            gravite: 2,
            badge: depuis === 0 ? "échue aujourd'hui" : `échue depuis ${depuis} j`,
            detail: `Mise en attente jusqu'au ${formatCourt(c.attenteJusquAu)}`
                + `${c.attenteMotif ? ` — ${c.attenteMotif}` : ''}. Le terme est passé : reprenez la `
                + `commande, ou repoussez l'attente.`,
        });
        return trierParGravite(out);
    }
    /*
     * **Une note ne fait plus taire une commande.**
     *
     * Le prototype endormait toute commande porteuse d'une note — « Réappro TPZ »,
     * « Att rglt », « Livraison Fin septembre » — tant qu'elle n'était pas partie.
     * Sans échéance : la commande ne revenait JAMAIS. Six commandes se taisaient
     * ainsi, dont trois depuis plus de cent jours, et l'une d'elles n'avait même
     * jamais été transmise à son fournisseur. La garde masquait au passage
     * l'alerte rouge « commande introuvable ».
     *
     * Une note reste une note : elle s'affiche, elle informe, elle explique un
     * `attente` quand aucune action n'est due. Ce qu'elle ne fait plus, c'est
     * décider à la place du moteur.
     *
     * Pour suspendre une commande, il y a `attenteJusquAu` ci-dessus : même
     * silence, mais avec un terme — et donc un retour.
     */
    /*
     * **Une commande reçue est à faire le jour même. Sans délai, sans seuil.**
     *
     * Un délai de grâce a existé ici un temps, adossé au réglage `envoiAlerte` —
     * « reçue depuis N jours sans envoi fournisseur ». C'était une erreur de
     * nature : transmettre une commande n'est pas une alerte qui se déclenche
     * quand on a trop attendu, c'est le travail ordinaire, et il commence à
     * l'arrivée. Retarder son apparition ne fait pas gagner de calme, cela fait
     * disparaître de la liste ce qu'il y a de plus simple à faire.
     *
     * Le réglage a donc été retiré plutôt que mis à zéro : le laisser dans
     * l'écran aurait été proposer de réintroduire le délai qu'on vient d'écarter.
     *
     * Ce qui reste réglable, c'est la COULEUR — à partir de quand le retard
     * devient orange, puis rouge —, et non le fait de figurer dans « À faire ».
     */
    if (etape === 'recue') {
        const age = joursEntre(c.dateReception, aujourdhui);
        /*
         * La gravité monte jusqu'au rouge, et c'est un correctif.
         *
         * Elle était plafonnée à 2, au motif qu'un envoi en retard « n'est jamais
         * rouge ». À l'usage c'est faux : `39356` attendait d'être transmise depuis
         * 80 jours, et se classait sous une livraison bloquée depuis 3 jours. Une
         * commande que personne n'a jamais passée est le pire état où une commande
         * puisse être — le client attend, et rien n'est en route.
         */
        const gravite = age >= s.envoiBloquant ? 3 : (age >= s.envoiCritique ? 2 : 1);
        /*
         * Une facture non réglée ne retire pas le geste de « À faire » : elle le
         * fait CONFIRMER.
         *
         * Passer une commande engage une dépense chez le fournisseur ; la passer
         * avant d'être payé, c'est avancer l'argent. Elle a un temps été différée
         * — sortie de « À faire », rangée dans « En cours » —, mais c'est dans
         * « À faire » qu'on la cherche pour la passer, et l'y cacher faisait oublier
         * des commandes qu'on comptait bien envoyer. Elle y reste donc, et c'est le
         * geste qui prévient : cliquer ouvre une confirmation avant la préparation
         * du mail.
         *
         * `paiementRegle` à `null` ne signale RIEN : c'est l'état avant le premier
         * relevé Sellsy, et traiter l'inconnu comme un impayé ferait confirmer
         * chaque envoi.
         */
        const blocage = c.paiementRegle === false
            ? "Facture non réglée : la commande peut être passée, après confirmation."
            : null;
        /*
         * Sauf si l'on a CHOISI d'attendre le règlement.
         *
         * C'est la différence avec le simple signal ci-dessus : là, la commande
         * reste due et le geste demande confirmation ; ici, quelqu'un a décidé de
         * ne pas la passer avant d'être payé. Elle ne réclame donc rien — elle
         * patiente dans « En cours » — jusqu'à ce que le relevé trouve la facture
         * réglée.
         */
        // Tout a été remboursé : il n'y a plus rien à transmettre.
        if (!attendLeReglement(c) && !c.rienACommander) {
            out.push({
                cle: 'fournisseur',
                titre: noms ? `Envoyer à ${noms}` : 'Envoyer au fournisseur',
                gravite,
                blocage,
                badge: age <= 0 ? "reçue aujourd'hui" : `reçue il y a ${age} j`,
                detail: `Reçue le ${formatCourt(c.dateReception)}, pas encore transmise${noms ? ` à ${noms}` : ''}.`
                    + (age >= s.envoiBloquant
                        ? ` Elle attend depuis ${age} jours : rien n'est en route, et le client attend toujours.`
                        : ''),
            });
        }
    }
    if (c.incident === 'COMMANDE_MANQUANTE') {
        const bloquee = c.dateEnvoiFournisseur ? `${joursEntre(c.dateEnvoiFournisseur, aujourdhui)} j bloquée` : 'bloquée';
        out.push({
            cle: 'probleme',
            titre: `Commande introuvable chez ${aupres(c)}`,
            gravite: 3,
            badge: bloquee,
            detail: `${noms || 'Le fournisseur'} a répondu « commande manquante ». À repasser ou à réclamer.`,
        });
    }
    if (etape === 'fournisseur' && c.incident === null && c.dateEnvoiFournisseur) {
        const attente = joursEntre(c.dateEnvoiFournisseur, aujourdhui);
        /*
         * **Chez HIOLLE, la suite est à nous.**
         *
         * HIOLLE stocke, il n'expédie pas : la marchandise attend que nous fassions
         * venir DHL. Relancer HIOLLE pour une date d'expédition — ce que faisait
         * l'action ci-dessous — réclamait à l'entrepôt ce qui dépend de nous.
         *
         * Due après `enlevementDelai` jours : le temps que HIOLLE prépare et
         * annonce ses palettes, sans lesquelles la demande ne se compose pas.
         * Avant, la commande patiente dans « En cours » — et la relance pour une
         * date reste écartée, puisque ce n'est pas d'eux que la date viendra.
         */
        const enlevementAttendu = chezHiolle(c.fournisseurs) && !c.enlevementReserve;
        if (enlevementAttendu && attente >= s.enlevementDelai) {
            out.push({
                cle: 'enlevement',
                // Sans transporteur : DHL pour des palettes, DPD pour des colis, et
                // seul le mail de HIOLLE le dira — à l'ouverture de la demande.
                titre: 'Demander l’enlèvement',
                // Orange quand le retard sur le délai atteint celui d'une relance.
                gravite: attente >= s.enlevementDelai + s.relanceFournisseur ? 2 : 1,
                badge: attente <= 0 ? "transmise aujourd'hui" : `transmise il y a ${attente} j`,
                detail: `Transmise à HIOLLE le ${formatCourt(c.dateEnvoiFournisseur)}. La marchandise `
                    + 'attend à l’entrepôt : c’est à nous de faire venir le transporteur.',
            });
        }
        /*
         * Une relance déjà partie ouvre un SURSIS : on laisse à l'autre partie le
         * délai de résolution de ce motif pour répondre, avant de réclamer de
         * nouveau. Sans lui, l'action reviendrait chaque jour alors qu'on vient de
         * solliciter quelqu'un.
         *
         * Le sursis se lit sur le journal des relances, et non plus sur la seule
         * `dateRelanceFournisseur` : c'est là que vit le MOTIF, et le délai en
         * dépend. Une commande que le fournisseur ne retrouve pas met plus de temps
         * à revenir qu'une simple date d'expédition.
         */
        const enSursis = litigeEnSursis(c, reglages, aujourdhui, ['relance', 'probleme']);
        if (attente >= s.relanceFournisseur && !enSursis && !enlevementAttendu) {
            const relances = c.relancesFournisseur > 0
                ? ` Déjà relancé ${c.relancesFournisseur} fois, la dernière le ${formatCourt(c.dateRelanceFournisseur)}.`
                : '';
            /*
             * Le portail du fournisseur ne connaît pas la commande.
             *
             * Ce n'est PAS « pas de date d'expédition » : une date manquante veut dire
             * qu'il traite la commande et tarde à annoncer un départ. Ici il ne l'a
             * jamais enregistrée — constaté sur `FAC-20260826-39440`, transmise à
             * FORTEMIX le 2 septembre et absente de leur portail sous les trois formes
             * d'identifiant. Réclamer une date n'y changerait rien : ce qu'il faut,
             * c'est la repasser.
             *
             * D'où une action de nature différente — `probleme`, dont le geste est
             * « Repasser la commande », qui rouvre le mail de commande complet.
             */
            if (c.absentPortailFournisseur) {
                out.push({
                    cle: 'probleme',
                    titre: `Commande absente du portail ${aupres(c)}`,
                    gravite: 3,
                    badge: `${attente} j sans trace`,
                    detail: `Transmise le ${formatCourt(c.dateEnvoiFournisseur)}, elle ne figure pas sur le `
                        + `portail de ${aupres(c)} — vérifié le ${formatCourt(c.absentPortailFournisseur)}. `
                        + `Ce n'est pas une date d'expédition qui manque : la commande n'y a jamais été `
                        + `enregistrée. À repasser.${relances}`,
                });
                return trierParGravite(out);
            }
            out.push({
                cle: 'relance',
                titre: `Relancer ${aupres(c)} — pas de date d'expédition`,
                // Un fournisseur qui ne répond pas à une relance n'est plus un simple
                // retard : c'est un problème.
                gravite: c.relancesFournisseur > 0 ? 3 : 2,
                badge: `${attente} j sans réponse`,
                detail: `Transmise le ${formatCourt(c.dateEnvoiFournisseur)}${c.note ? ` · ${c.note}` : ''}. `
                    + `Aucune date communiquée.${relances}`,
            });
        }
    }
    /**
     * Le tracking se réclame APRÈS `trackingRetard` jours, pas le jour du départ.
     *
     * Le seuil ne filtrait rien : il ne changeait que la couleur, et l'action
     * remontait dès qu'une date d'expédition était saisie. Or on saisit cette date
     * au moment où la commande part — le transporteur n'a pas encore rendu son
     * numéro, et l'écran réclamait donc quelque chose qui n'existait pas encore.
     * C'était le même défaut que la sentinelle du suivi muet : un seuil défini,
     * affiché dans Réglages, et jamais appliqué.
     *
     * `trackingRetard = 0` rétablit l'ancien comportement — réclamer dès le
     * départ —, ce qui donne au réglage un sens que l'ancien code lui refusait.
     */
    if (etape === 'expediee' && c.dateExpedition) {
        const depuis = joursEntre(c.dateExpedition, aujourdhui);
        if (depuis >= s.trackingRetard) {
            /*
             * Au-delà de `trackingBloquant`, l'absence de tracking devient rouge.
             *
             * Ce n'est pas une saisie en retard de plus : sans numéro, la commande
             * n'atteint JAMAIS l'étape `transit`, et tout le bloc ci-dessous — retard
             * annoncé, suivi muet, livraison bloquée — lui reste inaccessible. Elle
             * ne basculera pas davantage en livrée. Elle sort du radar pour de bon,
             * et rien d'autre ne viendra le signaler.
             */
            const aveugle = depuis >= s.trackingBloquant;
            /*
             * « Saisir » ou « réclamer », selon qu'il existe quelqu'un à qui le
             * demander.
             *
             * Le cas ordinaire est bien une saisie : le transporteur a rendu son
             * numéro, il reste à le recopier. Mais chez une société qui expédie
             * elle-même — R-TEK fabrique ET expédie —, il n'y a aucun transporteur,
             * et le numéro ne viendra que du fournisseur. Demander de « saisir » ce
             * que personne n'a communiqué décrit un geste impossible.
             */
            const aReclamer = expedieLuiMeme(reglages, c.fournisseurs);
            out.push({
                cle: 'tracking',
                titre: aReclamer
                    ? `Réclamer le numéro de tracking à ${aupres(c)}`
                    : 'Saisir le numéro de tracking',
                gravite: aveugle ? 3 : 2,
                badge: depuis === 0 ? "partie aujourd'hui" : `partie il y a ${depuis} j`,
                detail: "Sans tracking, le suivi automatique ne démarre pas et le client n'est pas prévenu."
                    + (aReclamer
                        ? ` ${aupres(c)} expédie lui-même : le numéro ne viendra d'aucun transporteur, `
                            + 'il est à lui demander.'
                        : '')
                    + (aveugle
                        ? ` Partie depuis ${depuis} jours sans numéro : aucune alerte de livraison ne peut se `
                            + `lever sur cette commande, et elle ne passera jamais en livrée d'elle-même.`
                        : ''),
            });
        }
    }
    // Une commande en transit ne réclame rien tant que le transporteur parle. Deux
    // conditions la font remonter, et l'une OU l'autre suffit :
    //
    // - **le transporteur a manqué SA date** — une date qu'il a lui-même annoncée
    //   ou convenue, donc un engagement, pas une supposition ;
    // - **il s'est tu** — rien depuis `suiviMuet` jours, comptés depuis son
    //   dernier point de suivi ou, s'il n'en a jamais publié, depuis le départ.
    //   Un colis parti ce matin n'est pas « bloqué » : il vient de partir.
    //
    // **L'estimation interne ne déclenche plus rien**, et c'est le correctif :
    // `dateExpedition + transit[transporteur]` est un délai moyen posé dans les
    // réglages, que personne n'a promis. Il faisait remonter en ROUGE des
    // commandes dont le transporteur donnait des nouvelles le matin même — la
    // commande FAC-20260808-39419 était à « J+9 » avec un statut du jour. Une
    // alerte qu'aucune nouvelle ne fait taire finit par ne plus être lue.
    //
    // L'estimation reste affichée (« livraison estimée le… », fiche et réglages) :
    // elle renseigne, elle n'alerte pas.
    if (etape === 'transit') {
        /*
         * Le transport aussi a droit à son sursis, et c'est un correctif.
         *
         * `escalade` ne s'appliquait qu'au fournisseur : relancer un transporteur
         * pour une livraison bloquée ne faisait taire l'alerte aucune journée, si
         * bien que la commande réclamait encore le lendemain de la réclamation. On
         * relançait, et l'écran redemandait de relancer.
         */
        if (litigeEnSursis(c, reglages, aujourdhui, ['livraison']))
            return trierParGravite(out);
        const estimee = livraisonEstimee(c, reglages);
        const annoncee = estimee?.source === 'transporteur' ? estimee : null;
        const retard = annoncee ? joursEntre(annoncee.date, aujourdhui) - s.toleranceLivraison : 0;
        /**
         * Depuis combien de jours le transporteur se tait.
         *
         * Compté depuis le dernier FAIT connu : son dernier point de suivi, ou à
         * défaut le départ du colis. Le prototype employait ici une sentinelle à 99
         * jours dès qu'aucun point n'existait — si bien qu'une commande partie le
         * matin, dont le transporteur n'avait évidemment encore rien publié,
         * remontait immédiatement comme « jamais mise à jour ». Elle n'était pas
         * bloquée : elle venait de partir.
         *
         * `null` quand on ne sait rien dater — ni point de suivi, ni départ. On ne
         * peut alors pas dire depuis combien de temps le silence dure, et inventer
         * une durée reviendrait à réclamer sur une supposition.
         */
        /*
         * Fortemix expédie de République tchèque : la marchandise met
         * NÉCESSAIREMENT 4 jours ouvrés à atteindre la France, et le transporteur
         * se tait souvent pendant ce trajet. Ce silence-là est le transit normal,
         * pas une livraison à réclamer — rien ne se lève avant son terme.
         */
        const finTransitFortemix = c.dateExpedition && estCommandeFortemix([], c.fournisseurs)
            ? ajouterJoursOuvres(c.dateExpedition, TRANSIT_FORTEMIX_JOURS_OUVRES)
            : null;
        const transitNormal = finTransitFortemix !== null && !estApres(aujourdhui, finTransitFortemix);
        const dernierSigne = c.dateStatutTransporteur ?? c.dateExpedition;
        const muet = dernierSigne ? joursEntre(dernierSigne, aujourdhui) : null;
        const jamaisDePoint = c.dateStatutTransporteur === null;
        /*
         * Un blocage signalé ne réclame qu'au terme de `suiviMuet`, compté depuis
         * le point de suivi qui l'annonce. Un « Différé - En attente de
         * rendez-vous » du jour est le transporteur qui travaille : il appellera
         * le client. FAC-20261013-39543 remontait en rouge le jour même du point.
         * Sans date de point, on ne sait pas depuis quand il dure : on signale.
         */
        const bloqueeEnSursis = Boolean(c.dateStatutTransporteur)
            && joursEntre(c.dateStatutTransporteur, aujourdhui) < s.suiviMuet;
        if (estLivraisonBloquee(c.statutTransporteur) && !bloqueeEnSursis) {
            out.push({
                cle: 'livraison',
                titre: 'Livraison bloquée',
                gravite: 3,
                badge: 'signalée par le transporteur',
                detail: c.statutTransporteur ?? '',
            });
        }
        else if (annoncee && retard > 0) {
            out.push({
                cle: 'livraison',
                titre: 'Contrôler la livraison',
                gravite: 3,
                badge: `J+${retard}`,
                detail: `Le transporteur annonçait le ${formatCourt(annoncee.date)} et n'a pas livré. `
                    + `Dernier point de suivi : ${c.statutTransporteur || 'aucun'}.`,
            });
        }
        else if (muet !== null && muet >= s.suiviMuet && !transitNormal) {
            out.push({
                cle: 'livraison',
                titre: 'Suivi sans mise à jour',
                gravite: 2,
                badge: jamaisDePoint ? `jamais mis à jour · ${muet} j` : `${muet} j sans point`,
                detail: `Le transporteur ne renvoie plus rien depuis ${jamaisDePoint ? `le départ, le ${formatCourt(c.dateExpedition)}` : formatCourt(c.dateStatutTransporteur)}.`,
            });
        }
    }
    /*
     * **Une relance restée sans réponse au terme de son délai revient dans
     * « À faire ».**
     *
     * Les règles par étape ci-dessus ne s'en chargeaient qu'en partie : une
     * livraison réclamée au transporteur, par exemple, ne se relevait de nouveau
     * que si le colis était déclaré bloqué ou le suivi muet. Un transporteur qui
     * publie des points sans livrer laissait la commande, échéance passée, dans
     * « En cours » — sortie de « Problèmes » sans rien réclamer, donc perdue.
     *
     * On ne lit que la relance la PLUS RÉCENTE de chaque motif : une relance plus
     * ancienne, échue, ne compte plus dès qu'on a relancé de nouveau. Et rien
     * n'est ajouté quand une règle d'étape a déjà levé l'action du motif.
     */
    if (!c.livre) {
        const vus = new Set();
        // `litigesDe` rend les relances de la plus récente à la plus ancienne.
        for (const l of litigesDe(c, reglages, aujourdhui)) {
            if (vus.has(l.motif))
                continue;
            vus.add(l.motif);
            if (!l.encours || l.dansLesDelais || !l.echeance)
                continue;
            const cle = ACTION_DU_MOTIF[l.motif];
            if (out.some((a) => a.cle === cle))
                continue;
            const depuis = joursEntre(l.echeance, aujourdhui);
            out.push({
                cle,
                titre: `Relance sans réponse — ${l.aupres}`,
                gravite: 3,
                badge: `échue depuis ${depuis} j`,
                detail: `Relancé${l.le ? ` le ${formatCourt(l.le)}` : ''} pour ${LIBELLE_MOTIF_RELANCE[l.motif]}, `
                    + `réponse attendue le ${formatCourt(l.echeance)} : rien n'est venu.`,
            });
        }
    }
    /*
     * **Les mails client à envoyer, devenus des tâches.**
     *
     * Plus rien ne se prépare tout seul : le relevé du portail fournisseur
     * trouvait une date d'expédition et préparait le mail dans la foulée,
     * sans que personne l'ait demandé — un clic sur « Actualiser » pouvait ainsi
     * produire trois mails. L'automatisme est retiré ; le travail, lui, ne
     * doit pas disparaître avec, sans quoi le client cesserait d'être prévenu et
     * rien ne le dirait.
     *
     * **Placé ICI, et non avec les échecs d'envoi du début.** Un échec dit que le
     * système a manqué quelque chose, et rien ne doit le masquer — d'où sa
     * priorité. Un mail à envoyer n'est pas une panne : c'est du travail
     * ordinaire, et il doit passer APRÈS un colis bloqué ou un transporteur muet,
     * avec lesquels il partage la gravité 2. L'ordre d'insertion tranche les
     * égalités, et il le faisait dans le mauvais sens.
     *
     * **Jamais sur une commande livrée.** Annoncer un départ ou un numéro de
     * suivi à quelqu'un qui a déjà reçu sa commande est pire que le silence :
     * c'est un message qui inquiète sans informer.
     */
    /*
     * **Plus jamais pour la date d'expédition** (02/10/2026). Une date relevée
     * chez Fortemix part seule chez le client ; une date saisie à la main l'est
     * par quelqu'un qui sait que la commande part, et ne pas prévenir est alors
     * un choix. Restaient les commandes dont la date avait été relevée AVANT
     * l'envoi automatique — FAC-20260930-39523 — : leur mail restait « à
     * préparer », et la tâche réclamait un mail que personne n'avait décidé
     * d'envoyer. Seul l'ÉCHEC d'un envoi lancé reste signalé, plus haut.
     */
    if (!c.livre) {
        for (const canal of CANAUX_CLIENT) {
            if (canal === 'expedition')
                continue;
            if (c.envois[canal] !== 'a_envoyer')
                continue;
            out.push({
                cle: 'mail',
                canal,
                titre: LIBELLE_ATTENTE_CANAL[canal],
                gravite: 2,
                badge: 'à envoyer',
                detail: DETAIL_ATTENTE_CANAL[canal],
            });
        }
        // Un colis ajouté sans que son mail parte — fenêtre refermée sans envoyer.
        // « Ajouter sans mail » l'écarte, et il ne figure alors pas ici.
        for (const k of c.colisAPrevenir ?? []) {
            if (k.echec)
                continue;
            out.push({
                cle: 'mail',
                canal: 'tracking',
                colis: k.tracking,
                titre: `Prévenir le client du colis ${k.tracking}`,
                gravite: 2,
                badge: 'à envoyer',
                detail: `Le colis ${k.tracking} est en route, et le client n'a pas son numéro de suivi. `
                    + 'Le mail se relit puis part depuis contact@.',
            });
        }
    }
    /*
     * **La demande d'avis n'est plus un bouton qui note, c'est une case qui
     * programme.** La cocher fixe une échéance — livraison + `avisDelai`,
     * repoussée au prochain jour ouvré — et le passage de 10 h appelle le
     * webhook ce jour-là. L'action reste due tant que personne n'a tranché.
     *
     * Elle disparaît de deux façons : cochée, elle est traitée ; passé
     * `avisJusqua` — deux semaines —, elle ne vaut plus la peine d'être posée.
     * Tout cela vit dans `avisEncoreCochable`, avec le reste des règles d'avis.
     */
    if (avisEncoreCochable(c, reglages, aujourdhui)) {
        const depuis = joursEntre(c.dateStatutTransporteur, aujourdhui);
        out.push({
            cle: 'avis',
            titre: 'Demander un avis',
            gravite: 1,
            badge: depuis === 0 ? "livrée aujourd'hui" : `livrée il y a ${depuis} j`,
            detail: `Livrée le ${formatCourt(c.dateStatutTransporteur)}. Cocher la case programme `
                + `l'envoi pour le ${formatCourt(echeanceAvis(c, reglages))}, à 10 h.`,
        });
    }
    return trierParGravite(out);
}
/** Trajet République tchèque → France d'une commande Fortemix, en jours ouvrés. */
export const TRANSIT_FORTEMIX_JOURS_OUVRES = 4;
/** Tri stable : à gravité égale, l'ordre d'insertion est conservé. */
function trierParGravite(actions) {
    return actions.slice().sort((a, b) => b.gravite - a.gravite);
}
/** Gravité de la commande = celle de son action la plus grave, 0 si aucune. */
export function graviteDe(actions) {
    return actions.length ? actions[0].gravite : 0;
}
/**
 * Les relances passées d'une commande, une entrée par geste.
 *
 * **La colonne « Litige » ne garde que le passé.** Elle mêlait autrefois deux
 * natures : les relances déjà faites, et des problèmes EN COURS — livraison
 * bloquée, envoi en échec, commande introuvable. Ces trois-là lèvent déjà une
 * action dans « À faire », où se lit ce qui reste à faire ; les répéter ici
 * brouillait la lecture des deux colonnes. Le présent a sa colonne, celle-ci
 * garde la trace.
 *
 * **Une relance, une entrée.** L'agrégat « 2 relances » sous une icône chiffrée
 * ne disait ni quand ni pourquoi. Chaque geste compte désormais pour lui-même,
 * avec CE QU'IL RÉCLAMAIT — une date d'expédition, une commande que le
 * fournisseur ne retrouve pas, une livraison — et sa date. Le moyen employé
 * reste au journal et à l'historique, où il atteste un geste fait hors de
 * l'outil ; il ne remonte pas ici, parce qu'il ne se lit pas d'un coup d'œil.
 *
 * Les commandes antérieures à ce journal n'ont que leurs compteurs : on en
 * reconstitue autant d'entrées, motif déduit de la cible.
 *
 * Fonction pure, et volontairement séparée des actions : une action dit ce
 * qu'il reste à faire, un litige dit ce qui s'est passé. Une commande relancée
 * deux fois puis expédiée n'a plus d'action, mais garde ses deux relances.
 */
export function litigesDe(c, reglages, aujourdhui) {
    const noms = nomFournisseurs(c.fournisseurs);
    /**
     * Ce qu'on réclamait est-il toujours attendu ?
     *
     * La question se tranche sur le MOTIF, pas sur la cible. Les deux se
     * confondent dans le cas ordinaire, mais pas chez HIOLLE Logistique, qui EST
     * le transport : on lui réclame une date d'expédition, la cible est donc le
     * transporteur et le motif `relance`. En jugeant sur la cible, cette relance
     * restait « en cours » jusqu'à la livraison — alors que la date d'expédition,
     * seule chose qu'on réclamait, était arrivée depuis longtemps. L'icône disait
     * une chose et la couleur une autre.
     */
    const encoreAttendu = (motif) => {
        if (c.livre)
            return false;
        switch (motif) {
            // On réclamait que le fournisseur enregistre enfin la commande.
            case 'probleme': return c.absentPortailFournisseur !== null || c.dateExpedition === null;
            // On réclamait une date d'expédition : elle est arrivée, ou non.
            case 'relance': return c.dateExpedition === null;
            // On réclamait le numéro de suivi : sa saisie y met fin, et rien d'autre.
            // Pas la livraison — `c.livre` est déjà traité plus haut — ni le temps qui
            // passe : une commande partie sans numéro ne s'en trouve aucun toute seule.
            case 'tracking': return !c.tracking;
            // On réclamait la livraison : seule la livraison y met fin.
            case 'livraison': return true;
        }
    };
    const entree = (r) => {
        const versQui = r.cible === 'fournisseur'
            ? (r.societe || noms || 'le fournisseur')
            : (c.transporteur ?? 'le transporteur');
        /*
         * Les relances d'avant ce champ n'ont pas de motif : on le déduit de la
         * cible, ce qui est exact dans le cas courant — on relance un fournisseur
         * pour une date d'expédition, un transporteur pour une livraison.
         */
        const motif = r.motif
            ?? (r.cible === 'fournisseur' ? 'relance' : 'livraison');
        /*
         * Jusqu'à quand on patiente, et donc jusqu'à quand la commande reste dans
         * « Problèmes » plutôt que de réclamer de nouveau.
         *
         * Une relance non datée n'ouvre AUCUN sursis : les entrées reconstituées
         * depuis les vieux compteurs n'ont pas de jour, et leur accorder un délai
         * reviendrait à faire taire une commande sur une durée qu'on n'a pas
         * mesurée.
         */
        const echeance = r.le ? ajouterJours(r.le, delaiResolution(reglages, motif)) : null;
        return {
            cle: r.cible === 'fournisseur' ? 'relance_fournisseur' : 'relance_transporteur',
            nombre: 1,
            encours: encoreAttendu(motif),
            motif,
            le: r.le,
            aupres: versQui,
            moyen: r.moyen,
            detail: r.detail ?? null,
            echeance,
            dansLesDelais: echeance !== null && encoreAttendu(motif) && !c.livre
                && !estApres(aujourdhui, echeance),
            titre: `Relancé pour ${LIBELLE_MOTIF_RELANCE[motif]} — ${versQui}`
                + (r.le ? ` le ${formatCourt(r.le)}` : '')
                // L'échéance est l'information neuve : elle dit jusqu'à quand on
                // patiente, et donc pourquoi la commande ne réclame rien aujourd'hui.
                + (echeance && encoreAttendu(motif) && !c.livre
                    ? (estApres(aujourdhui, echeance)
                        ? ` — réponse attendue le ${formatCourt(echeance)}, dépassée`
                        : ` — réponse attendue le ${formatCourt(echeance)}`)
                    : '')
                + (r.detail ? ` — ${r.detail}` : ''),
        };
    };
    if (c.relancesFaites.length) {
        // De la plus récente à la plus ancienne : c'est l'ordre dans lequel on les
        // relit, et la dernière est celle qui compte.
        return [...c.relancesFaites]
            .sort((a, b) => (b.le ?? '').localeCompare(a.le ?? ''))
            .map(entree);
    }
    /*
     * Repli pour les commandes d'avant ce journal : leurs compteurs disent
     * combien de relances ont eu lieu, et leur dernière date. On en reconstitue
     * autant d'entrées — la trace vaut mieux que le silence, et le moyen, qu'on
     * ignore, n'est de toute façon pas ce que la colonne montre.
     */
    const anciennes = [
        ...Array.from({ length: c.relancesFournisseur }, () => ({
            le: c.dateRelanceFournisseur, cible: 'fournisseur', moyen: 'inconnu',
        })),
        ...Array.from({ length: c.relancesTransporteur }, () => ({
            le: null, cible: 'transporteur', moyen: 'inconnu',
        })),
    ];
    return anciennes.map(entree);
}
/**
 * Délai de résolution d'un motif, avec repli sur celui du code.
 *
 * Un document enregistré avant cette table n'en porte pas ; le repli évite de
 * comparer des jours à `undefined`, ce qui ferait taire une commande pour
 * toujours ou jamais, selon le sens de la comparaison.
 */
export function delaiResolution(reglages, motif) {
    const d = reglages.resolutionLitige?.[motif];
    return typeof d === 'number' ? d : RESOLUTION_LITIGE_PAR_DEFAUT[motif];
}
/** L'action qui réclame de nouveau ce qu'une relance réclamait. */
const ACTION_DU_MOTIF = {
    relance: 'relance',
    probleme: 'probleme',
    tracking: 'tracking',
    livraison: 'livraison',
};
/**
 * La commande est-elle sous le sursis d'une relance récente ?
 *
 * `motifs` restreint aux litiges qui concernent le bloc appelant : réclamer une
 * livraison ne dispense pas de réclamer une date d'expédition, et l'inverse non
 * plus. Ce sont deux attentes distinctes, auprès de deux interlocuteurs.
 */
function litigeEnSursis(c, reglages, aujourdhui, motifs) {
    return litigesDe(c, reglages, aujourdhui)
        .some((l) => l.dansLesDelais && motifs.includes(l.motif));
}
/**
 * `true` si la commande vit un litige EN COURS, dans son délai de résolution.
 *
 * C'est ce que contient l'onglet « Problèmes » : non livrée, déjà relancée, et
 * pas encore au terme laissé à l'autre partie. Elle ne réclame donc rien — le
 * geste vient d'être fait — sans pour autant suivre son cours tranquillement.
 *
 * Le terme passé, elle en sort d'elle-même : dans « En cours » si ce qu'on
 * réclamait est arrivé, dans « À faire » sinon.
 */
export function enLitige(c, reglages, aujourdhui) {
    if (c.livre)
        return false;
    return litigesDe(c, reglages, aujourdhui).some((l) => l.dansLesDelais);
}
/**
 * Tout le calculé d'une commande, en un appel. C'est ce que l'API sérialise :
 * le front n'a rien à recalculer.
 */
/** La commande attend son règlement, et il n'est pas arrivé. */
export function attendLeReglement(c) {
    return Boolean(c.attenteReglement) && c.paiementRegle !== true;
}
export function calculer(brut, reglages, aujourdhui) {
    // Les coches manuelles de la fiche priment sur les données, pour le calcul
    // seulement : c'est ici, et nulle part ailleurs, qu'elles s'appliquent.
    const c = appliquerEtapesCochees(brut);
    const actions = actionsDe(c, reglages, aujourdhui);
    const etape = etapeDe(c, aujourdhui);
    return {
        etape,
        libelleEtape: libelleEtape(c, aujourdhui),
        detailEtape: detailEtape(c, reglages, aujourdhui),
        segment: segmentDe(etape),
        actions,
        gravite: graviteDe(actions),
        attente: attenteEnCours(c, reglages, aujourdhui),
        livraisonEstimee: livraisonEstimee(c, reglages),
        litiges: litigesDe(c, reglages, aujourdhui),
    };
}
//# sourceMappingURL=actions.js.map